import React, { FC } from 'react';

import { FIXED_GLANCE_KEY } from '@common/constant/courseConstant';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { CourseFixedGlanceItem } from 'src/types/courseFixedGlanceItem';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { IconSize } from '@components/atoms/fontAwesomeIcon/constants';
import ResultPanelEmptyData from '@components/store/data-display/resultPanel/resultPanelEmptyData';
import CourseFixedGlanceSkeleton from '@components/store/product/courseFixedGlance/courseFixedGlanceSkeleton';
import { useBreakpointV2 } from '@helpers/use';

type CourseAcademicFixedGlanceProps = {
  isLoading: boolean;
  glances: CourseFixedGlanceItem[];
};

const CourseAcademicFixedGlance: FC<CourseAcademicFixedGlanceProps> = ({ isLoading, glances }) => {
  const { t } = useTranslation('store_course');
  const { isMobile } = useBreakpointV2();

  const theme = useTheme();

  if (isLoading) {
    return <CourseFixedGlanceSkeleton />;
  }

  if (!isLoading && !glances.length) {
    return <ResultPanelEmptyData sx={{ height: { xs: 264, lg: 320 } }} />;
  }

  const isAuditCheck = (id: string) => {
    const auditCheckList = [
      FIXED_GLANCE_KEY.VIDEO_COUNT,
      FIXED_GLANCE_KEY.LIFE_TIME_LEARN,
      FIXED_GLANCE_KEY.ATTACHMENT_FILE_COUNT,
      FIXED_GLANCE_KEY.ARTICLE_COUNT,
      FIXED_GLANCE_KEY.CERTIFICATE_BUNDLE,
    ];
    return auditCheckList.includes(id);
  };

  return (
    <Box display="grid" gridTemplateColumns="1fr" gap={1}>
      <Box display="grid" gridTemplateColumns="2fr 1fr 1fr" sx={{ padding: 1 }} alignItems="center" minHeight={60}>
        <Typography variant={isMobile ? 'h6' : 'h5'}>
          {t('course_detail.course_description.course_fixed_glance.academic.title')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <FontAwesomeIcon
            icon="fa-light fa-book-open-cover"
            color={theme.palette.textV2.secondary}
            size={IconSize.sm}
          />
          <Typography variant="h6" color={theme.palette.textV2.secondary}>
            {t('course_detail.course_description.course_fixed_glance.academic.audit.title')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <FontAwesomeIcon icon="fa-light fa-graduation-cap" color={theme.palette.primary.main} size={IconSize.sm} />
          <Typography variant="h6" color={theme.palette.primary.main}>
            {t('course_detail.course_description.course_fixed_glance.academic.credit.title')}
          </Typography>
        </Box>
      </Box>
      {glances.map(({ id, icon, title, subTitle }) => {
        return (
          <Box
            key={title}
            display="grid"
            gridTemplateColumns="2fr 1fr 1fr"
            sx={{
              background: isAuditCheck(id) ? theme.palette.grey[100] : theme.palette.statePrimaryOverlay.selected,
              padding: 1,
              borderRadius: '8px',
            }}
            alignItems="center"
            minHeight={41}
          >
            <Box display="grid" gridTemplateColumns="30px 1fr" gap="8px" alignItems="start">
              <Box sx={{ width: '100%', height: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {icon}
              </Box>
              <Box>
                <Typography variant={isMobile ? 'h6' : 'h5'}>{title}</Typography>
                {subTitle ? (
                  <Typography variant={isMobile ? 'body2lanna' : 'body1lanna'} color={theme.palette.textV2.secondary}>
                    {subTitle}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Box display="flex" justifyContent="center">
              {isAuditCheck(id) && (
                <FontAwesomeIcon
                  icon="fa-solid fa-circle-check"
                  color={theme.palette.textV2.disabled}
                  size={IconSize.sm}
                />
              )}
            </Box>
            <Box display="flex" justifyContent="center">
              <FontAwesomeIcon icon="fa-solid fa-circle-check" color={theme.palette.primary.main} size={IconSize.sm} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CourseAcademicFixedGlance;
